import { getFieldErrors, getErrorMessage, NON_FIELD_KEY } from '@/services/api';

type SetError = (name: any, err: { type: string; message: string }) => void; // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * Apply parsed server-side validation errors to a react-hook-form ``setError``
 * and return an ordered list of human-readable messages for a summary banner.
 *
 * For each FIELD error (everything except the non-field key) it calls
 * ``setError(field, { type: 'server', message })`` so the message renders next
 * to the input. The returned array contains the non-field/top-level message
 * first (when present) followed by each field message, suitable for an
 * ``<ErrorSummary />``.
 *
 * If nothing can be parsed, returns ``[getErrorMessage(error)]`` so the caller
 * always has something to display.
 */
export function applyServerErrors(
  error: unknown,
  setError: SetError
): string[] {
  const fieldErrors = getFieldErrors(error);
  const keys = Object.keys(fieldErrors);
  if (keys.length === 0) {
    return [getErrorMessage(error)];
  }

  const messages: string[] = [];

  // Non-field / top-level message leads the summary but is not applied to a field.
  const nonField = fieldErrors[NON_FIELD_KEY];
  if (nonField) {
    messages.push(nonField);
  }

  for (const [field, message] of Object.entries(fieldErrors)) {
    if (field === NON_FIELD_KEY) continue;
    setError(field, { type: 'server', message });
    messages.push(message);
  }

  return messages;
}
